import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import { Stack } from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import GeneralService from '../../../../../../services/general';
import { IIncomeInvoiceResourceShortProps } from '../../../../../../props/finance/incomes/invoices/invoice';
import moment from 'moment';
import { ResponsiveContainer, CartesianGrid, XAxis, YAxis, Legend, Bar, BarChart, Tooltip } from 'recharts';

type IncomeInvoiceSummaryChartProps = {
    invoices?: IIncomeInvoiceResourceShortProps[];
}

const IncomeInvoiceSummaryChart: React.FC<IncomeInvoiceSummaryChartProps> = (props: IncomeInvoiceSummaryChartProps) => {
    const [data, setData] = React.useState<{
        name: string;
        paid: number;
        unpaid: number;
        totalAmount: number;
    }[]>([]);

    useEffect(() => {
        setData(groupData());
    }, [props.invoices]);

    const groupData = () => {
        const result: {
            name: string;
            paid: number;
            unpaid: number;
            totalAmount: number;
        }[] = [];

        let monthly: { key: string, invoices: IIncomeInvoiceResourceShortProps[] }[] = [];

        (props.invoices || []).forEach((invoice) => {
            const date = invoice.createdAt || invoice.dueDate;
            if (date) {
                const invoiceMonth = moment(date).format("YYYYMM");
                const idx = monthly.findIndex((m) => m.key === invoiceMonth);
                if (idx < 0) {
                    monthly.push({
                        key: invoiceMonth,
                        invoices: [invoice]
                    });
                } else if (monthly[idx]) {
                    monthly[idx].invoices.push(invoice);
                }
            }
        });

        monthly = monthly.sort((a, b) => (a.key > b.key) ? 1 : ((b.key > a.key) ? -1 : 0));
        monthly.forEach((month) => {
            const name = moment(month.key + '', 'YYYYMM').format("MMM YYYY").toUpperCase();
            let _data = { name, paid: 0, unpaid: 0, totalAmount: 0 };
            month.invoices.forEach((invoice) => {
                _data.paid += Number(invoice.paid || "0");
                _data.unpaid += Number(invoice.unpaid || "0");
                _data.totalAmount += Number(invoice.invoiceAmount || "0");
            });

            result.push(_data);
        });

        return result;
    };

    const renderLegend = (props: any) => {
        const { payload } = props;

        return <Stack horizontal tokens={{ childrenGap: 20 }} horizontalAlign='center' styles={{ root: { marginTop: 10 } }}>
            {payload.map((entry: any, index: number) => {
                let text = 'Paid Amount';
                let variant = '#218d21';

                if (entry.value === 'paid') {
                    text = 'Terbayar (Paid)';
                    variant = '#218d21';
                } else if (entry.value === 'unpaid') {
                    text = 'Belum Dibayar (Unpaid)';
                    variant = '#ae383e';
                }

                return <Stack key={`legend-${index}`} horizontal tokens={{ childrenGap: 6 }} verticalAlign='center'>
                    <Stack.Item className={styles.indicator} styles={{ root: { backgroundColor: variant } }}> </Stack.Item>
                    <Text size={'small'}>{text}</Text>
                </Stack>;
            })}
        </Stack>;
    };

    const renderXAxisTick = (props: any) => {
        const { x, y, payload } = props;
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={12} textAnchor="middle" fill="#666" fontSize={11}>
                    {payload.value}
                </text>
            </g>
        );
    };

    const renderYAxisTick = (props: any) => {
        const { x, y, payload } = props;
        let formatted = `Rp. ${GeneralService.getNumberWithSeparator(payload.value)}`;
        if (payload.value >= 1000000000) {
            formatted = `Rp. ${(payload.value / 1000000000).toFixed(1)} M`;
        } else if (payload.value >= 1000000) {
            formatted = `Rp. ${(payload.value / 1000000).toFixed(0)} Jt`;
        }

        return (
            <g transform={`translate(${x},${y})`}>
                <text x={-6} y={4} textAnchor="end" fill="#666" fontSize={11}>
                    {formatted}
                </text>
            </g>
        );
    };

    const renderTooltip = (props: any) => {
        if (props.active && props.payload && props.payload.length) {
            const paid = (props.payload.find((p: any) => p.dataKey === 'paid')?.value || 0);
            const unpaid = (props.payload.find((p: any) => p.dataKey === 'unpaid')?.value || 0);
            const total = Number(paid) + Number(unpaid);

            return <Stack tokens={{ childrenGap: 8 }} className={styles.tooltip}>
                <Label size={'small'}>{props.label}</Label>
                <Stack className='divider'></Stack>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign='space-between' verticalAlign='center'>
                        <Label size={'xsmall'}>Terbayar (Paid)</Label>
                        <Text size={'small'} className='color-green'>Rp. {GeneralService.getNumberWithSeparator(paid)}</Text>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign='space-between' verticalAlign='center'>
                        <Label size={'xsmall'}>Belum Dibayar (Unpaid)</Label>
                        <Text size={'small'} className='color-red'>Rp. {GeneralService.getNumberWithSeparator(unpaid)}</Text>
                    </Stack>
                </Stack>
                <Stack className='divider'></Stack>
                <Stack horizontal tokens={{ childrenGap: 5 }} horizontalAlign='space-between' verticalAlign='center'>
                    <Label size={'xsmall'}>Total Invoiced</Label>
                    <Text size={'small'} style={{ fontWeight: 600 }}>Rp. {GeneralService.getNumberWithSeparator(total)}</Text>
                </Stack>
            </Stack>;
        }

        return null;
    };

    return <Stack className={styles.container} tokens={{ childrenGap: 10 }} styles={{ root: { width: '100%' } }}>
        <Label size={'medium'}>Grafik Trend Pemasukan Invoice</Label>
        <Stack styles={{ root: { width: '100%', height: 320 } }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 10, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis tick={renderXAxisTick} dataKey="name" />
                    <YAxis tick={renderYAxisTick} />
                    <Tooltip content={renderTooltip} />
                    <Legend content={renderLegend} />
                    <Bar dataKey="paid" name="paid" stackId="invoices" fill="#218d21" radius={[0, 0, 0, 0]} barSize={40} />
                    <Bar dataKey="unpaid" name="unpaid" stackId="invoices" fill="#ae383e" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
            </ResponsiveContainer>
        </Stack>
    </Stack>;
};

export default IncomeInvoiceSummaryChart;
